/**
 * "Protected" PDF export — no machine-readable text, decoy letterforms on the page.
 *
 * WHAT IT DOES
 * ------------
 * Takes a normally-rendered PDF and rebuilds it as images, in two layers of
 * defence:
 *
 * 1. **No text layer.** Every page becomes a raster image, so `pdftotext`,
 *    PyPDF, pdf.js and the loaders behind most AI document pipelines find
 *    nothing, and copy/paste yields nothing.
 * 2. **Decoy letterforms.** The real text is masked out of the raster and
 *    redrawn in `decoyfont.ttf`, a hybrid-image face: a crisp decoy letter over
 *    a blurred shadow of the real one. At the raster resolution the decoy wins,
 *    so anything reading the pixels sees the wrong letters. Viewed or printed at
 *    normal size the blur wins and a person reads the real text.
 *
 * THE SIZE CONSTRAINT — why RASTER_SCALE is so high
 * -------------------------------------------------
 * Which layer of the hybrid wins depends purely on pixels-per-em in the final
 * image. Measured through this pipeline, typing "TECHCORP INC. AND JOHN DOE":
 *
 *   63px  (scale 6)  -> "TECHCQDRP LMC. AMB JOKN DOE"   still readable
 *   84px  (scale 8)  -> "IFONQDBF LMO. AMB JCKN"        decoyed
 *   105px (scale 10) -> "IFONQDBF LMO. NMB"             decoyed
 *
 * Substitution ramps in with size rather than switching on at a threshold, and
 * how badly a given word reads depends on which letters it contains, so scale 8
 * is the lowest that decoys reliably instead of only partially. This constant is
 * load-bearing, not a quality knob: lowering it walks the output straight back
 * to legible real text.
 *
 * Two things were tested and ruled out as factors, so don't re-tune them hoping
 * to buy back file size: the monospace-to-proportional squeeze below measures
 * 1.03 (negligible), and JPEG at q0.9 is visually identical to lossless. Size is
 * the only lever.
 *
 * Smaller runs stay only partly decoyed even here: 9pt table cells reach 72px
 * and 7.5pt footers 60px. Covering those too would need scale 11+ and roughly
 * double the file size again.
 *
 * LIMITS — be straight about these
 * --------------------------------
 * - The face covers space, A-Z and a-z only. Digits and punctuation fall through
 *   to a normal font, so dates, amounts and clause numbers stay legible to a
 *   machine. Only letters are decoyed.
 * - Downscaling defeats the decoy layer. Reducing the image pushes glyphs back
 *   under the crossover and the real text reappears, so any reader that resizes
 *   before OCR — which most vision pipelines do — sees through it. The no-text-
 *   layer defence still holds; the decoy layer does not.
 * - A reader who zooms far past 100% sees decoys too. Normal viewing and
 *   printing are unaffected.
 * - The face has only uppercase-style letterforms, so the copy loses the
 *   original's mixed case: "This Agreement" comes out as "THIS AGREEMENT".
 * - Pages are visibly grey and soft next to the vector original, and the file is
 *   far larger — measured ~670 KB per LETTER page, so a 10-page contract lands
 *   around 7 MB. Rasterising also takes ~1.7s per page.
 * - No selectable text, no Ctrl+F, and no screen-reader access to the content.
 */

import { Document, Image, Page, pdf, Text } from '@react-pdf/renderer';
import React from 'react';

/**
 * Render scale relative to the PDF's native 72 dpi. Eight puts 10.5pt body text
 * at 84px, the lowest size that decoys reliably. Do not lower it without
 * re-measuring — see the module header.
 */
const RASTER_SCALE = 8;

/**
 * The decoy effect lives in thin crisp outlines, which is exactly what JPEG
 * blurs away, so quality stays high despite the large canvas.
 */
const JPEG_QUALITY = 0.9;

/** Registered under a private family name so it can't collide with app CSS. */
const DECOY_FAMILY = '__ProtectedDecoy';
const DECOY_FONT_URL = '/fonts/decoy/decoyfont.ttf';

/**
 * Fill used to erase the original text before redrawing it as decoys. Documents
 * here are black-on-white; a letterhead with artwork *behind body text* (rather
 * than only in the header/footer band) would show these patches, so spot-check
 * any such template.
 */
const MASK_FILL = '#ffffff';

/** Sits in the text layer so a screen reader has something, and no content leaks. */
const PROTECTED_NOTICE =
  'This document is a protected copy. Its machine-readable text has been ' +
  'intentionally removed. Please contact the document owner if you need an ' +
  'accessible or machine-readable version.';

interface RasterPage {
  /** JPEG data URL of the rendered page. */
  dataUrl: string;
  /** Original page width in PDF points, so the copy keeps the same page size. */
  width: number;
  /** Original page height in PDF points. */
  height: number;
}

/** A single text run from pdf.js, in the shape we actually use. */
interface TextItem {
  str?: string;
  transform?: number[];
  width?: number;
  height?: number;
}

/**
 * Make the decoy face available to canvas. `fillText` renders COLR colour fonts
 * natively, which is what produces the decoy-over-blur effect — the same font
 * cannot be embedded in a PDF, because react-pdf's TrueType subsetter only
 * understands `glyf` outlines and throws on COLR glyphs.
 */
async function loadDecoyFont(): Promise<void> {
  const face = new FontFace(DECOY_FAMILY, `url('${DECOY_FONT_URL}')`);
  window.document.fonts.add(face);
  await face.load();
}

/**
 * Erase each real text run from the raster and redraw it in the decoy face.
 *
 * Coordinates follow the same rules as `detect-signature-anchors.ts`: pdf.js
 * gives a bottom-left origin in `transform[4]`/`transform[5]`, where `[5]` is the
 * text baseline.
 */
function drawDecoyText(
  ctx: CanvasRenderingContext2D,
  items: TextItem[],
  pageHeightPt: number
): void {
  ctx.textBaseline = 'alphabetic';

  for (const item of items) {
    const transform = item.transform;
    if (!item.str || !item.str.trim() || !transform) continue;

    const [a, b, c, d, e, f] = transform;

    // Rotated or skewed runs would need the full matrix to place correctly.
    // These documents have none; leave any we find untouched rather than
    // masking text we then draw in the wrong place.
    if (b !== 0 || c !== 0) continue;

    const fontSizePt = Math.abs(d) || item.height || 0;
    if (fontSizePt <= 0) continue;

    const runWidthPt = item.width ?? 0;
    const scaleX = Math.abs(a) || 1;

    const x = e * RASTER_SCALE;
    const baseline = (pageHeightPt - f) * RASTER_SCALE;
    const fontSizePx = fontSizePt * RASTER_SCALE;
    const runWidthPx = runWidthPt * RASTER_SCALE;

    // Cover ascenders and descenders, not just the em box.
    const padY = fontSizePx * 0.32;
    ctx.fillStyle = MASK_FILL;
    ctx.fillRect(
      x - 1,
      baseline - fontSizePx - padY * 0.5,
      (runWidthPx || fontSizePx * item.str.length * 0.6) + 2,
      fontSizePx + padY
    );

    ctx.font = `${fontSizePx}px "${DECOY_FAMILY}", Helvetica, sans-serif`;
    ctx.fillStyle = '#111827';

    // The decoy face is monospace while the source is proportional, so a run
    // would drift and overflow. Squeeze each run back onto the width pdf.js
    // reported for it, which keeps every line ending where it was.
    const measured = ctx.measureText(item.str).width;
    if (measured > 0 && runWidthPx > 0) {
      ctx.save();
      ctx.translate(x, baseline);
      ctx.scale(runWidthPx / measured, 1);
      ctx.fillText(item.str, 0, 0);
      ctx.restore();
    } else if (scaleX !== 1) {
      ctx.save();
      ctx.translate(x, baseline);
      ctx.scale(scaleX, 1);
      ctx.fillText(item.str, 0, 0);
      ctx.restore();
    } else {
      ctx.fillText(item.str, x, baseline);
    }
  }
}

/**
 * Render every page of `source` to a JPEG data URL, with the text decoyed.
 *
 * pdfjs-dist is imported lazily because evaluating it outside a browser blows up
 * on `DOMMatrix`, and so its ~3 MB runtime only ships when someone actually asks
 * for a protected download. The worker comes from the Vite-bundled `?url` asset
 * rather than a CDN, matching `extract-pdf-text.ts` and `detect-signature-anchors.ts`.
 */
async function rasterizePages(source: Blob): Promise<RasterPage[]> {
  if (typeof window === 'undefined') {
    throw new Error('buildProtectedPdf can only be used in a browser environment');
  }

  const [pdfjsLib, workerUrl] = await Promise.all([
    import('pdfjs-dist'),
    import('pdfjs-dist/build/pdf.worker.mjs?url').then((m) => (m as { default: string }).default),
    // Must resolve before the first fillText or the run silently falls back.
    loadDecoyFont(),
  ]);

  pdfjsLib.GlobalWorkerOptions.workerSrc = workerUrl;

  const data = await source.arrayBuffer();
  const loadingTask = pdfjsLib.getDocument({ data });
  const doc = await loadingTask.promise;
  const pages: RasterPage[] = [];

  try {
    for (let pageNumber = 1; pageNumber <= doc.numPages; pageNumber++) {
      const page = await doc.getPage(pageNumber);

      // scale 1 == PDF points, which is what the rebuilt <Page> needs.
      const pointSize = page.getViewport({ scale: 1 });
      const viewport = page.getViewport({ scale: RASTER_SCALE });

      const canvas = window.document.createElement('canvas');
      const context = canvas.getContext('2d');
      if (!context) {
        throw new Error('Failed to get canvas context');
      }

      canvas.width = Math.ceil(viewport.width);
      canvas.height = Math.ceil(viewport.height);

      // JPEG has no alpha channel — without an explicit white fill every
      // untouched pixel encodes as black.
      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, canvas.width, canvas.height);

      await page.render({ canvasContext: context, viewport, canvas }).promise;

      const textContent = await page.getTextContent();
      drawDecoyText(context, textContent.items as TextItem[], pointSize.height);

      pages.push({
        dataUrl: canvas.toDataURL('image/jpeg', JPEG_QUALITY),
        width: pointSize.width,
        height: pointSize.height,
      });

      // Free the backing store before allocating the next page; at this scale a
      // single LETTER canvas is ~31 megapixels (~124 MB while it is alive).
      canvas.width = 0;
      canvas.height = 0;
      page.cleanup();
    }
  } finally {
    await loadingTask.destroy();
  }

  return pages;
}

const ProtectedDocument: React.FC<{ pages: RasterPage[] }> = ({ pages }) => (
  <Document>
    {pages.map((page, index) => (
      <Page key={index} size={[page.width, page.height]}>
        {/* Drawn first so the full-bleed page image paints straight over it. */}
        <Text
          style={{
            position: 'absolute',
            top: 12,
            left: 12,
            right: 12,
            fontSize: 9,
            color: '#ffffff',
          }}
        >
          {PROTECTED_NOTICE}
        </Text>
        <Image
          src={page.dataUrl}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: page.width,
            height: page.height,
          }}
        />
      </Page>
    ))}
  </Document>
);

/**
 * Convert a normally-rendered PDF into a protected, image-only copy with decoy
 * letterforms.
 *
 * @param source - A rendered PDF blob, e.g. from `pdf(<LegalDocumentPDF …/>)`.
 */
export async function buildProtectedPdf(source: Blob): Promise<Blob> {
  const pages = await rasterizePages(source);

  if (pages.length === 0) {
    throw new Error('Source PDF contained no pages to protect');
  }

  return pdf(<ProtectedDocument pages={pages} />).toBlob();
}
